import {Component, OnInit, inject, output, input, effect} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ProjectStatus, Right, TeamRole, ViewMode} from "../../../interfaces";
import {DataService} from "../../../../services/data.service";
import {FormService} from "../../../../services/form.service";
import {ProjectStatusPipe} from "../../../../pipes/project-status.pipe";
import Swal from "sweetalert2";
import {AccessService} from "../../../../services/access.service";
import {ToastrService} from "ngx-toastr";
import {ProjectService} from "../../../../services/project.service";
import {NgIf, NgFor} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {DisableControlDirective} from '../../../../directives/disable-control.directive';
import {TeamRolePipe} from '../../../../pipes/team-role.pipe';

@Component({
    selector: 'app-project-organisation',
    templateUrl: './project-organisation.component.html',
    styleUrl: './project-organisation.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, DisableControlDirective, TeamRolePipe]
})
export class ProjectOrganisationComponent implements OnInit {

    accessService = inject(AccessService);
    formService = inject(FormService);
    projectService = inject(ProjectService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private projectStatusPipe = inject(ProjectStatusPipe);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;
    readonly Right = Right;
    readonly TeamRole = TeamRole;
    readonly ProjectStatus = ProjectStatus;

    teamRoleList = [];
    teamRoleListNew = [];

    viewMode!: ViewMode;
    project: any;
    form!: UntypedFormGroup;

    orderers: any[] = [];
    leads: any[] = [];
    customers: any[] = [];
    regions: any[] = [];
    departments: any[] = [];

    controlsDisabled = false;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.teamRoleList = Object.values(TeamRole);

        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            team: this.fb.array([]),
            id_region: [''],
            id_department: [''],

            department: [''],
            region: [''],
        });

        if (this.PID() !== '0') {
            this.getProject();
        }

        this.dataService.request('User/listForCreation').subscribe(response => {
            const users = response.users;

            this.orderers = users.filter(user => {
                return user.roles?.includes('orderer');
            });
            this.leads = users.filter(user => {
                return user.roles?.includes('lead');
            });
            this.customers = users.filter(user => {
                return user.roles?.includes('customer');
            });
        });

        this.dataService.request("Region/listForProject").subscribe(response => {
            this.regions = response.regions;
        });

        this.dataService.request("Department/listForProject").subscribe(response => {
            this.departments = response.departments;
        });
    }

    getProject(): void {
        this.dataService.request('Project/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();

            this.controlsDisabled = this.disableControls();
        });
    }

    disableControls(): boolean {
        if (this.accessService.hasRights(Right.PMO)) {
            return false;
        }

        return this.projectService.hasStatus(this.project, ProjectStatus.IN_PROGRESS)
            && this.accessService.hasRoles(this.project, TeamRole.Lead);
    }

    patchForm(): void {

        this.form.patchValue({
            id: this.project.id,
            id_region: this.project.id_region ?? '',
            id_department: this.project.id_department ?? '',

            department: this.project.department,
            region: this.project.region,
        });

        this.formService.getFormArray(this.form, 'team').clear();
        this.project.team.forEach(item => {
            this.addFormItem(item, 'team');
        });

        // add team entries if missing
        const formArray = this.formService.getFormArray(this.form, 'team');

        [TeamRole.Orderer, TeamRole.Lead, TeamRole.Customer, TeamRole.Employee].forEach(role => {
            if (!formArray.controls.find(control => {
                return control.get('role').value == role;
            })) {
                this.addFormItem({role: role}, 'team');
            }
        });
    }

    addFormItem(data: any = {}, control): void {
        const newGroup = this.fb.group({
            id: [data.id || null],
            id_project: [data.id_project || this.project.id],
            member: [data.member || null],
            member_alt: [data.member_alt || null],
            id_member: [data.id_member || ''],
            role: [data.role || '', Validators.required],
            _deleted: [false],
        });

        this.formService.getFormArray(this.form, control).push(newGroup);

        this.updateTeamRoleListNew();

        // sort team members by their role
        this.formService.getFormArray(this.form, control).patchValue(
            this.formService.getFormArray(this.form, control).value.sort((a, b) => {

                const roleMapping = {
                    orderer: 5,
                    lead: 4,
                    customer: 3,
                    employee: 2,
                    lead_external: 1,
                    employee_external: 0,
                }

                if (roleMapping[a.role] < roleMapping[b.role]) {
                    return 1;
                } else if (roleMapping[a.role] > roleMapping[b.role]) {
                    return -1;
                } else {
                    return 0;
                }

                // return roleMapping[a.role] <=> roleMapping[b.role];
            })
        );
    }

    updateTeamRoleListNew(): void {
        this.teamRoleListNew = Object.values(TeamRole);
        /*this.teamRoleListNew = this.teamRoleListNew.filter(role => {
            return role !== 'orderer';
        });*/
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    /** Checks whether a role can be deleted - only orderer */
    canBeDeleted(entry): boolean {
        return true;

        // allow deleting all roles as per CPR (09.05.2023)
        //return entry.value.role !== TeamRole.Orderer;

        /*if ([TeamRole.Orderer, TeamRole.Customer, TeamRole.Employee].includes(entry.value.role)) {

            const formArray = this.formService.getFormArray(this.form, 'team');

            const timesFound = formArray.controls.filter(control => {
                return control.get('role').value == entry.value.role && control.get('_deleted').value == false;
            }).length;

            return timesFound > 1;
        } else {
            return true;
        }*/
    }

    update(): void {
        if (![ProjectStatus.REVISE].includes(this.project.status)) {
            this.dataService.request('Project/updateOrganisation', {
                project: this.form.getRawValue(),
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        } else {
            Swal.fire({
                title: 'Sicher?',
                text: 'Der Status wird auf "' + this.projectStatusPipe.transform(ProjectStatus.IN_ELABORATION) + '" gesetzt.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein'
            }).then(result => {
                if (result.value) {
                    this.dataService.request('Project/setStatus', {
                        PID: this.PID(),
                        status: ProjectStatus.IN_ELABORATION,
                    }).subscribe(() => {
                        this.dataService.request('Project/updateOrganisation', {
                            project: this.form.getRawValue(),
                        }).subscribe(() => {
                            this.toastrService.success('Erfolgreich gespeichert')
                            this.setMode(ViewMode.VIEW);
                            this.getProject();
                            this.changeProject.emit();
                        });
                    });
                }
            });
        }
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }

        this.updateTeamRoleListNew();
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }
}
